import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const NAV_LINKS = [
  {
    label: 'Services', href: '/services', children: [
      { label: 'Chimney Cleaning', href: '/services/chimney-cleaning' },
      { label: 'Air Duct Cleaning', href: '/services/air-duct-cleaning' },
      { label: 'Dryer Vent Cleaning', href: '/services/dryer-vent-cleaning' },
    ],
  },
  { label: 'About', href: '/about' },
  { label: 'Process', href: '/process' },
  { label: 'Gallery', href: '/gallery' },
  { label: 'Service Areas', href: '/service-areas' },
  { label: 'Reviews', href: '/reviews' },
  { label: 'Blog', href: '/blog' },
  { label: 'Contact', href: '/contact' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const solidBg = scrolled || !isHome;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
        solidBg
          ? 'bg-white border-b border-stone-100'
          : 'bg-black/35 backdrop-blur-sm border-b border-white/10'
      }`}
    >
      {/* Top info bar — only visible on non-home or scrolled */}
      {solidBg && (
        <div className="bg-brand-dark border-b border-white/5 hidden lg:block">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5 flex items-center justify-between">
            <div className="flex items-center gap-5 text-xs text-white/60">
              <span className="flex items-center gap-1.5">
                <i className="ri-map-pin-line text-brand-amber" />
                Serving Jacksonville, FL &amp; Surrounding Areas
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-brand-amber" />
                Mon–Fri 8am–6pm · Sat 9am–3pm
              </span>
            </div>
            <div className="flex items-center gap-4 text-xs text-white/60">
              <span className="flex items-center gap-1.5">
                <i className="ri-verified-badge-fill text-brand-amber" />
                CSIA &amp; NADCA Certified
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-shield-check-fill text-brand-amber" />
                Fully Licensed &amp; Insured
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo / Wordmark */}
          <Link to="/" className="flex items-center flex-shrink-0 cursor-pointer" aria-label="BBM Services - Home">
            <div className={`rounded-xl overflow-hidden transition-all duration-300 ${solidBg ? '' : 'bg-white/95 p-1'}`}>
              <img
                src="https://static.readdy.ai/image/81164d3ba0f00c2a7e247a9f180c9b75/c93e9219dae2cf80448e9bdc5a16dd68.jpeg"
                alt="BBM Services — Chimney, Air Duct & Dryer Vent Cleaning Jacksonville FL"
                className="h-14 w-auto object-contain"
              />
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-5">
            {NAV_LINKS.map((link) =>
              link.children ? (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <button
                    className={`flex items-center gap-1 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      solidBg
                        ? 'text-brand-light hover:text-brand-amber'
                        : 'text-white/90 hover:text-white'
                    }`}
                  >
                    {link.label}
                    <i className={`ri-arrow-down-s-line text-xs transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {servicesOpen && (
                    <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl overflow-hidden border border-stone-100 py-1 z-50">
                      {link.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className="flex items-center gap-3 px-4 py-3 text-sm text-brand-light hover:text-brand-amber hover:bg-amber-50 transition-colors cursor-pointer"
                        >
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className="ri-arrow-right-s-line text-brand-amber" />
                          </div>
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    solidBg
                      ? 'text-brand-light hover:text-brand-amber'
                      : 'text-white/90 hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>

          {/* Header CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:+19045550191"
              className={`flex items-center gap-2 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap ${
                solidBg ? 'text-brand-dark hover:text-brand-amber' : 'text-white hover:text-amber-300'
              }`}
            >
              <div className="w-8 h-8 flex items-center justify-center bg-brand-amber rounded-lg">
                <i className="ri-phone-fill text-white text-sm" />
              </div>
              (904) 555-0191
            </a>
            <Link
              to="/contact"
              className="bg-brand-amber hover:bg-brand-amber-dark text-white text-sm font-bold px-5 py-2.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Get a Free Quote
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`lg:hidden p-2 rounded-lg transition-colors cursor-pointer ${
              solidBg ? 'text-brand-dark' : 'text-white'
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-xl`} />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-stone-100">
          <div className="px-4 py-4 space-y-1">
            {NAV_LINKS.map((link) =>
              link.children ? (
                <div key={link.label}>
                  <button
                    className="w-full flex items-center justify-between px-3 py-3 text-brand-dark font-medium text-sm cursor-pointer"
                    onClick={() => setServicesOpen(!servicesOpen)}
                  >
                    {link.label}
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`ri-arrow-down-s-line transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                    </div>
                  </button>
                  {servicesOpen && (
                    <div className="ml-4 space-y-1 pb-2 bg-amber-50/50 rounded-lg p-2">
                      {link.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className="block px-3 py-2 text-sm text-brand-warm-gray hover:text-brand-amber cursor-pointer"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className="block px-3 py-3 text-brand-dark font-medium text-sm hover:text-brand-amber cursor-pointer"
                >
                  {link.label}
                </Link>
              )
            )}
            <div className="pt-4 flex flex-col gap-3 border-t border-stone-100">
              <a
                href="tel:+19045550191"
                className="flex items-center justify-center gap-2 py-3 border-2 border-brand-amber text-brand-amber rounded-xl text-sm font-bold cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-fill" />
                </div>
                (904) 555-0191
              </a>
              <Link
                to="/contact"
                className="bg-brand-amber text-white text-center py-3 rounded-xl text-sm font-bold cursor-pointer"
              >
                Get a Free Quote
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
